import React from "react";
import ImagingArmSpeedSlider from "../Imager/ImagingArmSpeedSlider";
import "./ArmMover.css";
import { FourDirectionalPad, TwoDirectionalPad } from "./DPad/DPad";
import { EMoveArmDirection } from "../../Model/EMoveImagingArmButton";
import { EDPadButtonMode } from "./DPad/EDPadButtonMode";
import { E2DPadMode } from "./DPad/E2DPadMode";
import ButtonPressHandler from "./DPad/ButtonPressHandler";
import { KeyboardKeyHandler } from "./DPad/KeyboardKeyHandler";

interface CartesianArmMoverProps {
  armName?: string;
  axisLabel: string[];
  onButtonDown: (direction: EMoveArmDirection) => void;
  onButtonUp: () => void;
  x?: number;
  y?: number;
  z?: number;
  xSpeed?: number;
  ySpeed?: number;
  zSpeed?: number;
  setXSpeed?: (value: number) => void;
  setYSpeed?: (value: number) => void;
  setZSpeed?: (value: number) => void;
};

interface CylindricalArmMoverProps {
  armName?: string;
  axisLabel: string[];
  onButtonDown: (direction: EMoveArmDirection) => void;
  onButtonUp: () => void;
  t?: number;
  e?: number;
  z?: number;
  tSpeed?: number;
  eSpeed?: number;
  zSpeed?: number;
  setTSpeed?: (value: number) => void;
  setESpeed?: (value: number) => void;
  setZSpeed?: (value: number) => void;
};

interface ExtendedCylindricalArmMoverProps extends CartesianArmMoverProps {
  t?: number;
  tSpeed?: number;
  setTSpeed?: (value: number) => void;
}

export default class ArmMover {
  static getCartesianArmMover (props: CartesianArmMoverProps) : React.JSX.Element {
    return getCartesianArmMover(props);
  }

  static getCylindricalArmMover(props: CylindricalArmMoverProps): React.JSX.Element {
    return getCylindricalArmMover(props);
  }

  static getExtendedCylindricalArmMover(props: ExtendedCylindricalArmMoverProps): React.JSX.Element {
    return getExtendedCylindricalArmMover(props);
  }
}

function getCartesianArmMover({
  armName = "",
  axisLabel,
  onButtonDown,
  onButtonUp,
  x,
  y,
  z,
  xSpeed,
  ySpeed,
  zSpeed,
  setXSpeed,
  setYSpeed,
  setZSpeed,
}: CartesianArmMoverProps) {
  const buttonUpPressHandler: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.North),
    onButtonRelease: onButtonUp,
  };

  const buttonRightPressHandler: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.East),
    onButtonRelease: onButtonUp,
  };

  const buttonDownPressHandler: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.South),
    onButtonRelease: onButtonUp,
  };

  const buttonLeftPressHandler: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.West),
    onButtonRelease: onButtonUp,
  };

  const zControlButtonUp: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.Up),
    onButtonRelease: onButtonUp,
  };

  const zControlButtonDown: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.Down),
    onButtonRelease: onButtonUp,
  };

  return (
    <div className="arm-mover arm-mover-container">
      <span
        style={{
          gridArea: "current-position",
        }}
      >
        {armName + " Current Position"}
      </span>

      {x != null && (
        <div
          className="arm-position"
          style={{
            gridArea: "arm-position-x",
          }}
        >
          <span>{axisLabel[0]}</span>
          <input aria-readonly={true} value={x.toFixed(3)} readOnly={true} />
        </div>
      )}

      {y != null && (
        <div
          className="arm-position"
          style={{
            gridArea: "arm-position-y",
          }}
        >
          <span>{axisLabel[1]}</span>
          <input aria-readonly={true} value={y.toFixed(3)} readOnly={true} />
        </div>
      )}

      {z != null && (
        <div
          className="arm-position"
          style={{
            gridArea: "arm-position-z",
          }}
        >
          <span>{axisLabel[2]}</span>
          <input aria-readonly={true} value={z.toFixed(3)} readOnly={true} />
        </div>
      )}

      <div className="arm-mover-button-group">
        <div style={{ gridArea: "1" }}>
          <FourDirectionalPad
            verticalAxisLabel={axisLabel[1]}
            verticalAxisButtonMode={EDPadButtonMode.Directional}
            horizontalAxisLabel={axisLabel[0]}
            horizontalAxisButtonMode={EDPadButtonMode.Directional}
            buttonUpPressHandler={buttonUpPressHandler}
            buttonDownPressHandler={buttonDownPressHandler}
            buttonLeftPressHandler={buttonLeftPressHandler}
            buttonRightPressHandler={buttonRightPressHandler}
            keyboardShortcutUp={KeyboardKeyHandler.Create(
              [
                { key: "8", shift: false },
                { key: "ArrowUp", shift: false },
              ],
              buttonUpPressHandler.onButtonPress,
              buttonUpPressHandler.onButtonRelease
            )}
            keyboardShortcutRight={KeyboardKeyHandler.Create(
              [
                { key: "6", shift: false },
                { key: "ArrowRight", shift: false },
              ],
              buttonRightPressHandler.onButtonPress,
              buttonRightPressHandler.onButtonRelease
            )}
            keyboardShortcutDown={KeyboardKeyHandler.Create(
              [
                { key: "2", shift: false },
                { key: "ArrowDown", shift: false },
              ],
              buttonDownPressHandler.onButtonPress,
              buttonDownPressHandler.onButtonRelease
            )}
            keyboardShortcutLeft={KeyboardKeyHandler.Create(
              [
                { key: "4", shift: false },
                { key: "ArrowLeft", shift: false },
              ],
              buttonLeftPressHandler.onButtonPress,
              buttonLeftPressHandler.onButtonRelease
            )}
          />
        </div>
        <div>
          <TwoDirectionalPad
            label={axisLabel[2]}
            layout={E2DPadMode.Vertical}
            buttonMode={EDPadButtonMode.Directional}
            positiveDirButtonPressHandler={zControlButtonUp}
            negativeDirButtonPressHandler={zControlButtonDown}
            positiveDirKeyboardShortcut={KeyboardKeyHandler.Create(
              [
                { key: "9", shift: false },
                { key: "PageUp", shift: false },
              ],
              zControlButtonUp.onButtonPress,
              zControlButtonUp.onButtonRelease
            )}
            negativeDirKeyboardShortcut={KeyboardKeyHandler.Create(
              [
                { key: "3", shift: false },
                { key: "PageDown", shift: false },
              ],
              zControlButtonDown.onButtonPress,
              zControlButtonDown.onButtonRelease
            )}
          />
        </div>
      </div>
      {xSpeed != null && setXSpeed != null && (
        <div
          className="arm-speed"
          style={{
            gridArea: "arm-speed-slider-x",
          }}
        >
          {x != null && <span>{axisLabel[0]}</span>}
          <ImagingArmSpeedSlider value={xSpeed} onValueChanged={setXSpeed} />
        </div>
      )}
      {ySpeed != null && setYSpeed != null && (
        <div
          className="arm-speed"
          style={{
            gridArea: "arm-speed-slider-y",
          }}
        >
          {y != null && <span>{axisLabel[1]}</span>}
          <ImagingArmSpeedSlider value={ySpeed} onValueChanged={setYSpeed} />
        </div>
      )}
      {zSpeed != null && setZSpeed != null && (
        <div
          className="arm-speed"
          style={{
            gridArea: "arm-speed-slider-z",
          }}
        >
          {z != null && <span>{axisLabel[2]}</span>}
          <ImagingArmSpeedSlider value={zSpeed} onValueChanged={setZSpeed} />
        </div>
      )}
    </div>
  );
}

function getCylindricalArmMover({
  armName = "",
  axisLabel,
  onButtonDown,
  onButtonUp,
  t,
  e,
  z,
  tSpeed,
  eSpeed,
  zSpeed,
  setTSpeed,
  setESpeed,
  setZSpeed,
}: CylindricalArmMoverProps) {
  const buttonUpPressHandler: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.North),
    onButtonRelease: onButtonUp,
  };

  const buttonRightPressHandler: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.ClockWise),
    onButtonRelease: onButtonUp,
  };

  const buttonDownPressHandler: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.South),
    onButtonRelease: onButtonUp,
  };

  const buttonLeftPressHandler: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.CounterClockWise),
    onButtonRelease: onButtonUp,
  };

  const zControlButtonUp: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.Up),
    onButtonRelease: onButtonUp,
  };

  const zControlButtonDown: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.Down),
    onButtonRelease: onButtonUp,
  };

  return (
    <div className="arm-mover arm-mover-container">
      <span
        style={{
          gridArea: "current-position",
        }}
      >
        {armName + " Current Position"}
      </span>

      {t != null && (
        <div
          className="arm-position"
          style={{
            gridArea: "arm-position-x",
          }}
        >
          <span>{axisLabel[0]}</span>
          <input aria-readonly={true} value={t.toFixed(3)} readOnly={true} />
        </div>
      )}

      {e != null && (
        <div
          className="arm-position"
          style={{
            gridArea: "arm-position-y",
          }}
        >
          <span>{axisLabel[1]}</span>
          <input aria-readonly={true} value={e.toFixed(3)} readOnly={true} />
        </div>
      )}

      {z != null && (
        <div
          className="arm-position"
          style={{
            gridArea: "arm-position-z",
          }}
        >
          <span>{axisLabel[2]}</span>
          <input aria-readonly={true} value={z.toFixed(3)} readOnly={true} />
        </div>
      )}

      <div className="arm-mover-button-group">
        <FourDirectionalPad
          verticalAxisLabel={axisLabel[1]}
          verticalAxisButtonMode={EDPadButtonMode.Directional}
          horizontalAxisLabel={axisLabel[0]}
          horizontalAxisButtonMode={EDPadButtonMode.Rotational}
          buttonUpPressHandler={buttonUpPressHandler}
          buttonDownPressHandler={buttonDownPressHandler}
          buttonLeftPressHandler={buttonLeftPressHandler}
          buttonRightPressHandler={buttonRightPressHandler}
          keyboardShortcutUp={KeyboardKeyHandler.Create(
            [
              { key: "8", shift: false },
              { key: "ArrowUp", shift: false },
            ],
            buttonUpPressHandler.onButtonPress,
            buttonUpPressHandler.onButtonRelease
          )}
          keyboardShortcutRight={KeyboardKeyHandler.Create(
            [
              { key: "6", shift: false },
              { key: "ArrowRight", shift: false },
            ],
            buttonRightPressHandler.onButtonPress,
            buttonRightPressHandler.onButtonRelease
          )}
          keyboardShortcutDown={KeyboardKeyHandler.Create(
            [
              { key: "2", shift: false },
              { key: "ArrowDown", shift: false },
            ],
            buttonDownPressHandler.onButtonPress,
            buttonDownPressHandler.onButtonRelease
          )}
          keyboardShortcutLeft={KeyboardKeyHandler.Create(
            [
              { key: "4", shift: false },
              { key: "ArrowLeft", shift: false },
            ],
            buttonLeftPressHandler.onButtonPress,
            buttonLeftPressHandler.onButtonRelease
          )}
        />
        <TwoDirectionalPad
          label={axisLabel[2]}
          layout={E2DPadMode.Vertical}
          buttonMode={EDPadButtonMode.Directional}
          positiveDirButtonPressHandler={zControlButtonUp}
          negativeDirButtonPressHandler={zControlButtonDown}
          positiveDirKeyboardShortcut={KeyboardKeyHandler.Create(
            [
              { key: "9", shift: false },
              { key: "PageUp", shift: false },
            ],
            zControlButtonUp.onButtonPress,
            zControlButtonUp.onButtonRelease
          )}
          negativeDirKeyboardShortcut={KeyboardKeyHandler.Create(
            [
              { key: "3", shift: false },
              { key: "PageDown", shift: false },
            ],
            zControlButtonDown.onButtonPress,
            zControlButtonDown.onButtonRelease
          )}
        />
      </div>
      {tSpeed != null && setTSpeed != null && (
        <div
          className="arm-speed"
          style={{
            gridArea: "arm-speed-slider-x",
          }}
        >
          {t != null && <span>{axisLabel[0]}</span>}
          <ImagingArmSpeedSlider value={tSpeed} onValueChanged={setTSpeed} />
        </div>
      )}
      {eSpeed != null && setESpeed != null && (
        <div
          className="arm-speed"
          style={{
            gridArea: "arm-speed-slider-y",
          }}
        >
          {e != null && <span>{axisLabel[1]}</span>}
          <ImagingArmSpeedSlider value={eSpeed} onValueChanged={setESpeed} />
        </div>
      )}
      {zSpeed != null && setZSpeed != null && (
        <div
          className="arm-speed"
          style={{
            gridArea: "arm-speed-slider-z",
          }}
        >
          {z != null && <span>{axisLabel[2]}</span>}
          <ImagingArmSpeedSlider value={zSpeed} onValueChanged={setZSpeed} />
        </div>
      )}
    </div>
  );
}

function getExtendedCylindricalArmMover({
  armName = "",
  axisLabel,
  onButtonDown,
  onButtonUp,
  x,
  y,
  z,
  t,
  xSpeed,
  ySpeed,
  zSpeed,
  tSpeed,
  setXSpeed,
  setYSpeed,
  setZSpeed,
  setTSpeed,
}: ExtendedCylindricalArmMoverProps) {
  const buttonUpPressHandler: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.North),
    onButtonRelease: onButtonUp,
  };

  const buttonRightPressHandler: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.East),
    onButtonRelease: onButtonUp,
  };

  const buttonDownPressHandler: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.South),
    onButtonRelease: onButtonUp,
  };

  const buttonLeftPressHandler: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.West),
    onButtonRelease: onButtonUp,
  };

  const zControlButtonUp: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.Up),
    onButtonRelease: onButtonUp,
  };

  const zControlButtonDown: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.Down),
    onButtonRelease: onButtonUp,
  };

  const cwButtonDown: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.ClockWise),
    onButtonRelease: onButtonUp
  }

  const ccwButtonDown: ButtonPressHandler = {
    onButtonPress: () => onButtonDown(EMoveArmDirection.CounterClockWise),
    onButtonRelease: onButtonUp,
  };

  return (
    <div className="arm-mover arm-mover-container">
      <span
        style={{
          gridArea: "current-position",
        }}
      >
        {armName + " Current Position"}
      </span>

      {x != null && (
        <div
          className="arm-position"
          style={{
            gridArea: "arm-position-x",
          }}
        >
          <span>{axisLabel[0]}</span>
          <input aria-readonly={true} value={x.toFixed(3)} readOnly={true} />
        </div>
      )}

      {y != null && (
        <div
          className="arm-position"
          style={{
            gridArea: "arm-position-y",
          }}
        >
          <span>{axisLabel[1]}</span>
          <input aria-readonly={true} value={y.toFixed(3)} readOnly={true} />
        </div>
      )}

      {z != null && (
        <div
          className="arm-position"
          style={{
            gridArea: "arm-position-z",
          }}
        >
          <span>{axisLabel[2]}</span>
          <input aria-readonly={true} value={z.toFixed(3)} readOnly={true} />
        </div>
      )}

      {t != null && (
        <div
          className="arm-position"
          style={{
            gridArea: "arm-position-t",
          }}
        >
          <span>{axisLabel[3]}</span>
          <input aria-readonly={true} value={t.toFixed(3)} readOnly={true} />
        </div>
      )}

      <div className="arm-mover-button-group">
        <div style={{ gridArea: "area1" }}>
          <FourDirectionalPad
            verticalAxisLabel={axisLabel[1]}
            verticalAxisButtonMode={EDPadButtonMode.Directional}
            horizontalAxisLabel={axisLabel[0]}
            horizontalAxisButtonMode={EDPadButtonMode.Directional}
            buttonUpPressHandler={buttonUpPressHandler}
            buttonDownPressHandler={buttonDownPressHandler}
            buttonLeftPressHandler={buttonLeftPressHandler}
            buttonRightPressHandler={buttonRightPressHandler}
            keyboardShortcutUp={KeyboardKeyHandler.Create(
              [
                { key: "8", shift: false },
                { key: "ArrowUp", shift: false },
              ],
              buttonUpPressHandler.onButtonPress,
              buttonUpPressHandler.onButtonRelease
            )}
            keyboardShortcutRight={KeyboardKeyHandler.Create(
              [
                { key: "6", shift: false },
                { key: "ArrowRight", shift: false },
              ],
              buttonRightPressHandler.onButtonPress,
              buttonRightPressHandler.onButtonRelease
            )}
            keyboardShortcutDown={KeyboardKeyHandler.Create(
              [
                { key: "2", shift: false },
                { key: "ArrowDown", shift: false },
              ],
              buttonDownPressHandler.onButtonPress,
              buttonDownPressHandler.onButtonRelease
            )}
            keyboardShortcutLeft={KeyboardKeyHandler.Create(
              [
                { key: "4", shift: false },
                { key: "ArrowLeft", shift: false },
              ],
              buttonLeftPressHandler.onButtonPress,
              buttonLeftPressHandler.onButtonRelease
            )}
          />
        </div>
        <div style={{ gridArea: "area2" }}>
          <TwoDirectionalPad
            label={axisLabel[2]}
            layout={E2DPadMode.Vertical}
            buttonMode={EDPadButtonMode.Directional}
            positiveDirButtonPressHandler={zControlButtonUp}
            negativeDirButtonPressHandler={zControlButtonDown}
            positiveDirKeyboardShortcut={KeyboardKeyHandler.Create(
              [
                { key: "9", shift: false },
                { key: "PageUp", shift: false },
              ],
              zControlButtonUp.onButtonPress,
              zControlButtonUp.onButtonRelease
            )}
            negativeDirKeyboardShortcut={KeyboardKeyHandler.Create(
              [
                { key: "3", shift: false },
                { key: "PageDown", shift: false },
              ],
              zControlButtonDown.onButtonPress,
              zControlButtonDown.onButtonRelease
            )}
          />
        </div>
        <div style={{ gridArea: "area3" }}>
          <TwoDirectionalPad
            label={axisLabel[3]}
            layout={E2DPadMode.Horizontal}
            buttonMode={EDPadButtonMode.Rotational}
            positiveDirButtonPressHandler={cwButtonDown}
            negativeDirButtonPressHandler={ccwButtonDown}
            positiveDirKeyboardShortcut={KeyboardKeyHandler.Create(
              [
                { key: "7", shift: false },
                { key: "Home", shift: false },
              ],
              cwButtonDown.onButtonPress,
              cwButtonDown.onButtonRelease
            )}
            negativeDirKeyboardShortcut={KeyboardKeyHandler.Create(
              [
                { key: "1", shift: false },
                { key: "End", shift: false },
              ],
              ccwButtonDown.onButtonPress,
              ccwButtonDown.onButtonRelease
            )}
          />
        </div>
      </div>
      {xSpeed != null && setXSpeed != null && (
        <div
          className="arm-speed"
          style={{
            gridArea: "arm-speed-slider-x",
          }}
        >
          {x != null && <span>{axisLabel[0]}</span>}
          <ImagingArmSpeedSlider value={xSpeed} onValueChanged={setXSpeed} />
        </div>
      )}
      {ySpeed != null && setYSpeed != null && (
        <div
          className="arm-speed"
          style={{
            gridArea: "arm-speed-slider-y",
          }}
        >
          {y != null && <span>{axisLabel[1]}</span>}
          <ImagingArmSpeedSlider value={ySpeed} onValueChanged={setYSpeed} />
        </div>
      )}
      {zSpeed != null && setZSpeed != null && (
        <div
          className="arm-speed"
          style={{
            gridArea: "arm-speed-slider-z",
          }}
        >
          {z != null && <span>{axisLabel[2]}</span>}
          <ImagingArmSpeedSlider value={zSpeed} onValueChanged={setZSpeed} />
        </div>
      )}
      {tSpeed != null && setTSpeed != null && (
        <div
          className="arm-speed"
          style={{
            gridArea: "arm-speed-slider-t",
          }}
        >
          {t != null && <span>{axisLabel[3]}</span>}
          <ImagingArmSpeedSlider value={tSpeed} onValueChanged={setTSpeed} />
        </div>
      )}
    </div>
  );
}
